// src/lib/services/liveIndexService.ts
import { db } from '../firebase';
import {
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  Unsubscribe,
  getDocs,
  limit as firestoreLimit,
} from 'firebase/firestore';
import { LiveIndexEntry, LiveSource, DiscoverySection } from '../../live/types/liveIndex';
import { resolveLiveAudienceContract } from '../../live/types/liveAudience';
import { resolveLiveDeliveryContract, resolveLiveDeliveryMode } from '../../live/types/liveDelivery';
import { coerceTimestamp } from '../../live/utils/coerceTimestamp';

// ── Helpers ─────────────────────────────────────────────────────

const getStartedAtMillis = (entry: LiveIndexEntry) =>
  coerceTimestamp(entry.startedAt);

const toTimestampMillis = (value: unknown): number | null => {
  if (value == null) return null;
  const ms = coerceTimestamp(value);
  return ms === 0 ? null : ms;
};

const sortByStartedAtDesc = (lives: LiveIndexEntry[]) =>
  lives.slice().sort((a, b) => getStartedAtMillis(b) - getStartedAtMillis(a));

/** Filter out ended/inactive lives — safety net for when Cloud Function cleanup is delayed */
const filterActiveLives = (lives: LiveIndexEntry[]) =>
  lives.filter((live) => live.status === 'live');

const dedupeLives = (lives: LiveIndexEntry[]) => {
  const map = new Map<string, LiveIndexEntry>();
  lives.forEach((live) => map.set(live.id, live));
  return Array.from(map.values());
};

const canUserSeeLive = (live: LiveIndexEntry, userId?: string) => {
  if (live.visibility !== 'private') return true;
  if (!userId) return false;
  if (live.hostId === userId) return true;
  return Array.isArray(live.allowedUsers) && live.allowedUsers.includes(userId);
};

// ── Contract normalization (cached) ─────────────────────────────

const normalizationCache = new Map<string, LiveIndexEntry>();

const buildNormCacheKey = (live: LiveIndexEntry): string =>
  `${live.id}|${live.source}|${live.shortsMode ?? ''}|${live.broadcastMeeting ?? ''}|${live.deliveryMode ?? ''}|${live.audienceContract?.deliveryKind ?? ''}|${live.deliveryContract?.mode ?? ''}`;

const normalizeIndexEntry = (live: LiveIndexEntry): LiveIndexEntry => {
  const key = buildNormCacheKey(live);
  const cached = normalizationCache.get(key);
  if (cached && cached.id === live.id) return { ...live, audienceContract: cached.audienceContract, deliveryMode: cached.deliveryMode, deliveryContract: cached.deliveryContract };

  const normalized: LiveIndexEntry = {
    ...live,
    audienceContract: resolveLiveAudienceContract({
      source: live.source,
      shortsMode: live.shortsMode,
      broadcastMeeting: live.broadcastMeeting,
      audienceContract: live.audienceContract,
    }),
    deliveryMode: resolveLiveDeliveryMode({
      source: live.source,
      shortsMode: live.shortsMode,
      broadcastMeeting: live.broadcastMeeting,
      deliveryMode: live.deliveryMode,
      deliveryContract: live.deliveryContract,
    }),
    deliveryContract: resolveLiveDeliveryContract({
      source: live.source,
      shortsMode: live.shortsMode,
      broadcastMeeting: live.broadcastMeeting,
      deliveryMode: live.deliveryMode,
      deliveryContract: live.deliveryContract,
    }),
  };

  // Keep cache bounded
  if (normalizationCache.size > 500) normalizationCache.clear();
  normalizationCache.set(key, normalized);
  return normalized;
};

// ── Event filtering ─────────────────────────────────────────────

const filterEventsByStatus = (
  lives: LiveIndexEntry[],
  liveEventIds: Set<string>,
  enabled: boolean
) => {
  if (!enabled) {
    return lives.filter((live) => live.sourceCollection !== 'liveEvents');
  }
  return lives.filter((live) =>
    live.sourceCollection !== 'liveEvents' ? true : liveEventIds.has(live.id)
  );
};

const STALE_EVENT_MS = 12 * 60 * 60 * 1000; // 12 hours

const subscribeToLiveEvents = (
  onChange: (ids: Set<string>) => void,
): Unsubscribe => {
  const eventsRef = collection(db, 'liveEvents');
  const eventsQuery = query(eventsRef, where('status', '==', 'live'));
  return onSnapshot(eventsQuery, (snapshot) => {
    const now = Date.now();
    const ids = new Set(
      snapshot.docs
        .filter((docSnap) => {
          const startMs = toTimestampMillis(docSnap.data().startedAt);
          if (startMs === null) return true;
          return (now - startMs) < STALE_EVENT_MS;
        })
        .map((docSnap) => docSnap.id),
    );
    onChange(ids);
  }, (error) => {
    console.error('Error subscribing to live events:', error);
    onChange(new Set());
  });
};

// ── Emit helper ─────────────────────────────────────────────────

/** Emit lives directly — Cloud Functions manage liveIndex lifecycle, no double-check needed. */
const emitLives = (lives: LiveIndexEntry[], callback: (lives: LiveIndexEntry[]) => void) => {
  callback(sortByStartedAtDesc(filterActiveLives(dedupeLives(lives))));
};

// ── Subscription APIs ───────────────────────────────────────────

/**
 * Subscribe to all public lives (for Dashboard LiveNow)
 */
export function subscribeToPublicLives(
  callback: (lives: LiveIndexEntry[]) => void,
  _userId?: string
): Unsubscribe {
  const livesRef = collection(db, 'liveIndex');
  let indexLives: LiveIndexEntry[] = [];
  let eventLiveIds = new Set<string>();
  let eventLiveReady = false;

  const emit = () => {
    const normalized = indexLives.map(normalizeIndexEntry);
    const strict = filterEventsByStatus(normalized, eventLiveIds, eventLiveReady);
    emitLives(strict, callback);
  };

  const eventsUnsub = subscribeToLiveEvents((ids) => {
    eventLiveIds = ids;
    eventLiveReady = true;
    emit();
  });

  const primaryQuery = query(
    livesRef,
    where('visibility', '==', 'public'),
    orderBy('startedAt', 'desc')
  );

  const primaryUnsub = onSnapshot(primaryQuery, (snapshot) => {
    indexLives = snapshot.docs.map((doc) => doc.data() as LiveIndexEntry);
    emit();
  }, (error) => {
    console.error('Error subscribing to public lives:', error);
    callback([]);
  });

  return () => {
    primaryUnsub();
    eventsUnsub();
  };
}

/**
 * Subscribe to lives by source(s) (for filtered tabs)
 */
export function subscribeToLivesBySource(
  sources: LiveSource[],
  callback: (lives: LiveIndexEntry[]) => void
): Unsubscribe {
  if (!sources.length) {
    callback([]);
    return () => {};
  }

  const livesRef = collection(db, 'liveIndex');
  let indexLives: LiveIndexEntry[] = [];
  let eventLiveIds = new Set<string>();
  let eventLiveReady = false;
  const wantsEvents = sources.includes('event');

  const emit = () => {
    const normalized = indexLives.map(normalizeIndexEntry);
    const strict = filterEventsByStatus(normalized, eventLiveIds, eventLiveReady && wantsEvents);
    emitLives(strict, callback);
  };

  let eventsUnsub: Unsubscribe | null = null;
  if (wantsEvents) {
    eventsUnsub = subscribeToLiveEvents((ids) => {
      eventLiveIds = ids;
      eventLiveReady = true;
      emit();
    });
  }

  const primaryQuery = query(
    livesRef,
    where('visibility', '==', 'public'),
    where('source', 'in', sources),
    orderBy('startedAt', 'desc')
  );

  const primaryUnsub = onSnapshot(primaryQuery, (snapshot) => {
    indexLives = snapshot.docs.map((doc) => doc.data() as LiveIndexEntry);
    emit();
  }, (error) => {
    console.error('Error subscribing to lives by source:', error);
    callback([]);
  });

  return () => {
    primaryUnsub();
    eventsUnsub?.();
  };
}

/**
 * Subscribe to lives visible to a specific user (includes private they're allowed in)
 */
export function subscribeToVisibleLives(
  userId: string,
  callback: (lives: LiveIndexEntry[]) => void
): Unsubscribe {
  let publicLives: LiveIndexEntry[] = [];
  let privateLives: LiveIndexEntry[] = [];
  const livesRef = collection(db, 'liveIndex');
  let eventLiveIds = new Set<string>();
  let eventLiveReady = false;

  const mergeLives = () => {
    const merged = dedupeLives([...publicLives, ...privateLives]).map(normalizeIndexEntry);
    const strict = filterEventsByStatus(merged, eventLiveIds, eventLiveReady);
    const visible = strict.filter((live) => canUserSeeLive(live, userId));
    emitLives(visible, callback);
  };

  const eventsUnsub = subscribeToLiveEvents((ids) => {
    eventLiveIds = ids;
    eventLiveReady = true;
    mergeLives();
  });

  // Query public lives
  const publicQuery = query(
    livesRef,
    where('visibility', '==', 'public'),
    orderBy('startedAt', 'desc')
  );

  const publicUnsub = onSnapshot(publicQuery, (snapshot) => {
    publicLives = snapshot.docs.map((doc) => doc.data() as LiveIndexEntry);
    mergeLives();
  }, (error) => {
    console.error('Error subscribing to public lives:', error);
    publicLives = [];
    mergeLives();
  });

  // Query private lives user is allowed in
  const privateQuery = query(
    livesRef,
    where('visibility', '==', 'private'),
    where('allowedUsers', 'array-contains', userId),
    orderBy('startedAt', 'desc')
  );

  const privateUnsub = onSnapshot(privateQuery, (snapshot) => {
    privateLives = snapshot.docs.map((doc) => doc.data() as LiveIndexEntry);
    mergeLives();
  }, (error) => {
    console.error('Error subscribing to private lives:', error);
    privateLives = [];
    mergeLives();
  });

  return () => {
    publicUnsub();
    privateUnsub();
    eventsUnsub();
  };
}

/**
 * Subscribe to lives in a specific discovery section (trending, rising, most_engaging).
 * Returns up to 6 entries sorted by rankingScore descending.
 */
export function subscribeToDiscoverySection(
  section: DiscoverySection,
  callback: (lives: LiveIndexEntry[]) => void,
  _userId?: string,
): Unsubscribe {
  if (!section) {
    callback([]);
    return () => {};
  }

  const livesRef = collection(db, 'liveIndex');
  let latestLives: LiveIndexEntry[] = [];
  let eventLiveIds = new Set<string>();
  let eventLiveReady = false;

  const baseQuery = query(
    livesRef,
    where('visibility', '==', 'public'),
    firestoreLimit(60)
  );

  const emit = () => {
    const normalized = latestLives.map(normalizeIndexEntry);
    const filtered = filterEventsByStatus(normalized, eventLiveIds, eventLiveReady);
    const lives = filtered
      .filter((live) => live.status !== 'ended' && live.discoverySection === section)
      .sort((a, b) => (b.rankingScore || 0) - (a.rankingScore || 0))
      .slice(0, 6);
    callback(lives);
  };

  const eventsUnsub = subscribeToLiveEvents((ids) => {
    eventLiveIds = ids;
    eventLiveReady = true;
    emit();
  });

  const unsub = onSnapshot(
    baseQuery,
    (snapshot) => {
      latestLives = snapshot.docs.map((doc) => doc.data() as LiveIndexEntry);
      emit();
    },
    (error) => {
      console.error(`Error subscribing to discovery section "${section}":`, error);
      callback([]);
    }
  );

  return () => {
    unsub();
    eventsUnsub();
  };
}

// ── Host Replays (Studio Past Streams) ──────────────────────────

export interface HostReplaySummary {
  id: string;
  title: string;
  roomType: string;
  description?: string;
  thumbnailUrl?: string;
  isActive?: boolean;
  replayDeletedAt?: unknown;
  startedAt?: number;
  endedAt?: unknown;
  recording?: Partial<import('../../live/types/recording').RecordingState>;
}

interface HostReplayQueryOptions {
  includeDeleted?: boolean;
}

const normalizeHostReplays = (
  docs: Array<{ id: string; data(): unknown }>,
  options?: HostReplayQueryOptions,
): HostReplaySummary[] => {
  const all = docs.map((d) => ({ id: d.id, ...(d.data() as Record<string, unknown>) } as HostReplaySummary));
  return all
    .filter((r) => r.isActive === false)
    .filter((r) => options?.includeDeleted ? true : !r.replayDeletedAt)
    .filter((r) => r.recording && r.recording.status && r.recording.status !== 'idle')
    .sort((a, b) => (toTimestampMillis(b.startedAt) || 0) - (toTimestampMillis(a.startedAt) || 0));
};

export async function getHostReplays(userId: string, options?: HostReplayQueryOptions): Promise<HostReplaySummary[]> {
  const q = query(
    collection(db, 'liveRooms'),
    where('hostId', '==', userId),
  );
  const snap = await getDocs(q);
  return normalizeHostReplays(snap.docs, options);
}

export function subscribeToHostReplays(
  userId: string,
  callback: (replays: HostReplaySummary[]) => void,
  onError?: (error: Error) => void,
  options?: HostReplayQueryOptions,
): Unsubscribe {
  const q = query(
    collection(db, 'liveRooms'),
    where('hostId', '==', userId),
  );

  return onSnapshot(q, (snapshot) => {
    callback(normalizeHostReplays(snapshot.docs, options));
  }, (error) => {
    console.error('Error subscribing to host replays:', error);
    onError?.(error);
  });
}
